import { MediaitemModel } from '@models';
import { MediaitemService } from '@services';
import { useNetworkState } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import MediaitemAccessError, { MediaitemErrorCode } from './MediaitemAccessError';

const MediaitemImageThumbnail = ({
  mediaitem,
  type = 'small',
  imageClassName,
  onImageLoaded
}: {
  mediaitem: MediaitemModel;
  type: 'small' | 'large';
  imageClassName?: string;
  onImageLoaded?: () => void;
}) => {
  const network = useNetworkState();
  const [accessError, setAccessError] = useState({
    hasAccessError: false,
    errorCode: undefined
  });
  const mediaitemService = new MediaitemService(mediaitem);
  const src = mediaitemService.getImageSrc(true);

  const onImageError = (e: SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    if (target.naturalWidth === 0 && target.naturalHeight === 0) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_SRC_NOT_SUPPORTED
      });
    } else {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    }
  };

  useEffect(() => {
    setAccessError({
      hasAccessError: false,
      errorCode: undefined
    });
  }, [mediaitem]);

  useEffect(() => {
    if (!network.online) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    }
  }, [network.online]);

  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete) {
      onImageLoaded?.();
    }
  }, []);

  if (accessError.hasAccessError) {
    return (
      <MediaitemAccessError
        type='image'
        className={type === 'small' ? 'w-[150px] h-[150px]' : 'w-full aspect-square'}
        errorCode={accessError.errorCode}
      />
    );
  }

  return (
    src && (
      <div
        className={classNames('relative overflow-hidden', type === 'small' ? 'w-fit' : 'w-full')}
      >
        <img
          ref={imgRef}
          alt='image thumbnail'
          src={src}
          onError={onImageError}
          onLoad={onImageLoaded}
          className={classNames(
            'relative',
            'rounded-xs',
            type === 'small'
              ? 'max-w-[300px] h-auto object-contain'
              : 'w-full h-auto object-contain',
            imageClassName
          )}
        />
      </div>
    )
  );
};

export default MediaitemImageThumbnail;
